import type { CookDate } from '@platewell/core';
import {
  COOK_DATE_WINDOW_FUTURE,
  COOK_DATE_WINDOW_PAST,
  cookDateIdsOn,
  cookDateWindowRows,
  formatCookDay,
  formatCookDayShort,
  summariseCookDates,
} from '@platewell/core';
import { useState } from 'react';
import { Dialog } from './dialog.js';

export interface CookDateDialogProps {
  title: string;
  cookDates: readonly CookDate[];
  readOnly: boolean;
  onSet: (date: string) => void;
  onClear: (cookDateIds: readonly string[]) => void;
  onClose: () => void;
}

/**
 * Scheduling window: the 2 days before today, today, and the 10 days after.
 *
 * Clicking a day toggles whether the recipe is logged for that calendar date. The picker
 * at the bottom toggles the same way, so it reaches — and can clear — any date outside
 * the window.
 *
 * Saving is a commit: the caller confirms it and closes this dialog. Clearing is not,
 * because a day cleared by mistake is worth being able to put straight back.
 *
 * Mirrored on mobile by `apps/mobile/src/components/cook-date-sheet.tsx`; keep the two
 * in step.
 */
export function CookDateDialog({
  title,
  cookDates,
  readOnly,
  onSet,
  onClear,
  onClose,
}: CookDateDialogProps) {
  // With a mouse, a pick commits as soon as it lands. On touch screens iOS Safari fills in
  // a value and fires change while its picker is still open, so there the pick is held
  // until the picker closes and the input blurs.
  const commitOnBlur = window.matchMedia('(pointer: coarse)').matches;
  const [pickedDate, setPickedDate] = useState('');
  const rows = cookDateWindowRows(
    cookDates,
    undefined,
    COOK_DATE_WINDOW_PAST,
    COOK_DATE_WINDOW_FUTURE,
  );
  const summary = summariseCookDates(cookDates);

  /** Logged days outside the window have no row, so the summary stands in for them. */
  const summaryParts = [
    summary.lastMade != null ? `Last made · ${formatCookDay(summary.lastMade)}` : null,
    summary.timesMade > 0 ? `Times made · ${summary.timesMade}` : null,
  ].filter((part) => part != null);

  function toggle(date: string, cookDateIds: readonly string[]) {
    if (cookDateIds.length > 0) onClear(cookDateIds);
    else onSet(date);
  }

  /** Cleared after every pick so the same date can be chosen twice running — once to add
   *  it, once to clear it — which is how an out-of-window date gets removed. */
  function commitPicked(date: string) {
    if (date === '') return;
    setPickedDate('');
    toggle(date, cookDateIdsOn(cookDates, date));
  }

  return (
    <Dialog onClose={onClose} aria-label={`Cook dates for “${title}”`}>
      <h2>Cook dates</h2>
      <p className="hint">
        {readOnly
          ? 'You are offline in read-only mode, so these days cannot be changed.'
          : 'Pick a day to log it. Open this again and pick a logged day to remove it.'}
      </p>
      {summaryParts.length > 0 && <p className="hint">{summaryParts.join(' · ')}</p>}

      <ul className="cook-date-list">
        {rows.map((row) => (
          <li key={row.date}>
            <button
              type="button"
              className={`cook-date-day${row.selected ? ' selected' : ''}`}
              disabled={readOnly}
              aria-pressed={row.selected}
              aria-label={
                row.selected
                  ? `Clear ${row.date}`
                  : row.isFuture
                    ? `Plan for ${row.date}`
                    : `Mark made on ${row.date}`
              }
              onClick={() => toggle(row.date, row.cookDateIds)}
            >
              <span className="cook-date-label">
                <span>{formatCookDayShort(row.date)}</span>
                {row.isToday && <span className="hint"> · today</span>}
              </span>
              {row.selected && (
                <span className="cook-date-state">{row.isFuture ? 'Planned' : 'Made'}</span>
              )}
            </button>
          </li>
        ))}

        <li className="cook-date-picker">
          <label htmlFor="cook-date-picker-input">Another date</label>
          <input
            id="cook-date-picker-input"
            type="date"
            value={pickedDate}
            disabled={readOnly}
            onChange={(event) => {
              if (commitOnBlur) setPickedDate(event.target.value);
              else commitPicked(event.target.value);
            }}
            onBlur={() => commitPicked(pickedDate)}
          />
        </li>
      </ul>

      <div className="row" style={{ marginTop: 18 }}>
        <span className="spacer" />
        <button type="button" className="btn" onClick={onClose}>
          Done
        </button>
      </div>
    </Dialog>
  );
}
